import { useState } from 'react';
import { api, errorMessage } from '../../../api';
import { fmt } from '../../../format';
import { useSession } from '../../../session';
import type { WarehouseView } from '../../../types';
import { ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../../ui';
import { Icon } from '../../../ui/icons';
import { WarehouseEditor } from './Editor';
import { Labels } from './Labels';
import { Locations } from './Locations';
import { PackingSetup } from './PackingSetup';

type Tab = 'map' | 'locations' | 'packing' | 'labels';

/**
 * Depo tanımı: the floor plan where racks and areas are added and placed at their real size,
 * the bins (list, bulk edits), packing tables and cartons, and the bin labels.
 */
export function WarehouseBuilder() {
  const session = useSession();
  const warehouses = useLoad(() => api<WarehouseView[]>('GET', '/api/admin/warehouses'), []);
  const [picked, setPicked] = useState<string | null>(session.warehouseId);
  const [tab, setTab] = useState<Tab>('map');
  const [labelCodes, setLabelCodes] = useState<readonly string[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [version, setVersion] = useState(0);

  if (warehouses.error) return <ErrorBanner message={warehouses.error} onRetry={() => void warehouses.reload()} />;
  if (!warehouses.data) return <Spinner />;
  const warehouse = warehouses.data.find((w) => w.id === picked) ?? warehouses.data[0];
  const changed = () => {
    setVersion((v) => v + 1);
    void warehouses.reload();
  };
  const printLabels = (codes: readonly string[]) => {
    setLabelCodes(codes);
    setTab('labels');
  };

  return (
    <>
      <div className="row no-print">
        <select className="select" style={{ maxWidth: 300 }} value={warehouse?.id ?? ''} onChange={(e) => setPicked(e.target.value)} aria-label="Depo">
          {warehouses.data.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} · {w.name}
            </option>
          ))}
        </select>
        {warehouse && (
          <span className="muted">
            {fmt.n(warehouse.activeLocations)} aktif göz{warehouse.locations > warehouse.activeLocations ? ` · ${fmt.n(warehouse.locations - warehouse.activeLocations)} pasif` : ''}
          </span>
        )}
        <span className="spacer" style={{ flex: 1 }} />
        {session.isAdmin && (
          <button className="btn" onClick={() => setCreating(true)}>
            <Icon name="plus" /> Yeni depo
          </button>
        )}
      </div>
      <div className="tabs no-print" role="tablist">
        {(
          [
            ['map', 'Harita: raflar ve alanlar'],
            ['locations', 'Gözler'],
            ['packing', 'Paketleme masaları ve koliler'],
            ['labels', 'Etiket bas'],
          ] as const
        ).map(([key, label]) => (
          <button key={key} className={tab === key ? 'on' : ''} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>
      {!warehouse ? (
        <div className="banner info">Henüz depo yok. "Yeni depo" ile ilk deponuzu açın.</div>
      ) : tab === 'map' ? (
        <WarehouseEditor key={warehouse.id} warehouse={warehouse} onChanged={changed} onPrint={printLabels} />
      ) : tab === 'locations' ? (
        <Locations key={`${warehouse.id}|${version}`} warehouse={warehouse} onChanged={changed} onPrint={printLabels} />
      ) : tab === 'packing' ? (
        <PackingSetup key={`${warehouse.id}|${version}`} warehouse={warehouse} />
      ) : (
        <Labels key={warehouse.id} warehouse={warehouse} codes={labelCodes} onClearCodes={() => setLabelCodes(null)} />
      )}
      {creating && (
        <NewWarehouse
          onClose={() => setCreating(false)}
          onCreated={(w) => {
            setCreating(false);
            setPicked(w.id);
            setTab('map');
            void session.reload();
            changed();
          }}
        />
      )}
    </>
  );
}

function NewWarehouse({ onClose, onCreated }: { onClose: () => void; onCreated: (w: WarehouseView) => void }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [axis, setAxis] = useState<'x' | 'y'>('y');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const w = await api<WarehouseView>('POST', '/api/admin/warehouses', { code, name, layout: { aisleAxis: axis, crossAisles: [], depot: { x: 0, y: 0, z: 0 } } });
      toast(`${w.code} deposu açıldı; şimdi gözlerini oluşturun`, 'ok');
      onCreated(w);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Yeni depo"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !/^[A-Za-z0-9]{2,10}$/.test(code) || !name.trim()} onClick={() => void save()}>
            Depoyu aç
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <div className="form-grid">
          <div className="field">
            <label htmlFor="w-code">Depo kodu</label>
            <input id="w-code" className="input mono" maxLength={10} value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="IST2" />
          </div>
          <div className="field">
            <label htmlFor="w-name">Depo adı</label>
            <input id="w-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="İstanbul Hadımköy Depo" />
          </div>
          <div className="field">
            <label htmlFor="w-axis">Koridorlar hangi yönde uzanıyor</label>
            <select id="w-axis" className="select" value={axis} onChange={(e) => setAxis(e.target.value as 'x' | 'y')}>
              <option value="y">Derinliğe doğru (y ekseni)</option>
              <option value="x">Enine (x ekseni)</option>
            </select>
          </div>
        </div>
        <p className="muted" style={{ fontSize: 13 }}>
          Depo açıldıktan sonra Harita sekmesinde raf sıralarını ve alanları (mal kabul, paketleme masaları, sevk, iade, hasarlı) ekleyip yerlerine
          sürükleyin. Koridor yönü, raf numaralarının hangi yönde arttığını belirler; sonradan değiştirmeyin.
        </p>
      </div>
    </Modal>
  );
}
